import {
  Inviter,
  Invitation,
  Registerer,
  RegistererState,
  SessionState,
  UserAgent,
  type Session,
} from "sip.js";

export type SoftphoneStatus =
  | "offline"
  | "connecting"
  | "ready"
  | "ringing-in"
  | "calling"
  | "in-call";

export interface SoftphoneEvents {
  status: SoftphoneStatus;
  remoteNumber: string | null;
}

type Listener = (state: SoftphoneEvents) => void;

const SIP_DOMAIN = "crm.k-panel.ru";

/**
 * Thin wrapper around sip.js: one registration, at most one active call.
 * UI subscribes via onChange and gets a serializable state snapshot.
 */
export class Softphone {
  private userAgent: UserAgent | null = null;
  private registerer: Registerer | null = null;
  private session: Session | null = null;
  private audio: HTMLAudioElement;
  private listeners = new Set<Listener>();
  private muted = false;

  status: SoftphoneStatus = "offline";
  remoteNumber: string | null = null;

  constructor(audio: HTMLAudioElement) {
    this.audio = audio;
  }

  onChange(listener: Listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit() {
    const snapshot = { status: this.status, remoteNumber: this.remoteNumber };
    this.listeners.forEach((listener) => listener(snapshot));
  }

  private setStatus(status: SoftphoneStatus, remoteNumber?: string | null) {
    this.status = status;
    if (remoteNumber !== undefined) this.remoteNumber = remoteNumber;
    this.emit();
  }

  async connect(server: string, username: string, password: string) {
    if (this.userAgent) return;
    this.setStatus("connecting");
    const uri = UserAgent.makeURI(`sip:${username}@${SIP_DOMAIN}`);
    if (!uri) throw new Error("Invalid SIP URI");

    this.userAgent = new UserAgent({
      uri,
      transportOptions: { server },
      authorizationUsername: username,
      authorizationPassword: password,
      delegate: {
        onInvite: (invitation) => this.handleInvite(invitation),
      },
      logLevel: "warn",
    });

    await this.userAgent.start();
    this.registerer = new Registerer(this.userAgent, { expires: 300 });
    this.registerer.stateChange.addListener((state) => {
      if (
        state === RegistererState.Registered &&
        this.status === "connecting"
      ) {
        this.setStatus("ready");
      }
      if (state === RegistererState.Unregistered && this.status === "ready") {
        this.setStatus("offline");
      }
    });
    await this.registerer.register();
  }

  private handleInvite(invitation: Invitation) {
    if (this.session) {
      // busy: reject the second call
      invitation.reject({ statusCode: 486 }).catch(() => {});
      return;
    }
    this.session = invitation;
    const from = invitation.remoteIdentity.uri.user ?? "";
    this.watchSession(invitation);
    this.setStatus("ringing-in", from);
  }

  private watchSession(session: Session) {
    session.stateChange.addListener((state) => {
      if (state === SessionState.Established) {
        this.attachMedia(session);
        this.setStatus("in-call");
      }
      if (state === SessionState.Terminated) {
        this.session = null;
        this.muted = false;
        this.setStatus("ready", null);
      }
    });
  }

  private attachMedia(session: Session) {
    const sdh = session.sessionDescriptionHandler as unknown as {
      peerConnection?: RTCPeerConnection;
    };
    const pc = sdh?.peerConnection;
    if (!pc) return;
    const remoteStream = new MediaStream();
    pc.getReceivers().forEach((receiver) => {
      if (receiver.track) remoteStream.addTrack(receiver.track);
    });
    this.audio.srcObject = remoteStream;
    this.audio.play().catch(() => {});
  }

  async call(rawNumber: string) {
    if (!this.userAgent || this.session) return;
    const digits = rawNumber.replace(/\D/g, "");
    const number =
      digits.length === 11 && digits.startsWith("8")
        ? "7" + digits.slice(1)
        : digits.length === 10
          ? "7" + digits
          : digits;
    const target = UserAgent.makeURI(`sip:${number}@${SIP_DOMAIN}`);
    if (!target) throw new Error("Invalid number");
    const inviter = new Inviter(this.userAgent, target, {
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },
      },
    });
    this.session = inviter;
    this.watchSession(inviter);
    this.setStatus("calling", number);
    await inviter.invite();
  }

  async answer() {
    const session = this.session;
    if (session instanceof Invitation) {
      await session.accept({
        sessionDescriptionHandlerOptions: {
          constraints: { audio: true, video: false },
        },
      });
    }
  }

  async decline() {
    const session = this.session;
    if (session instanceof Invitation) {
      await session.reject({ statusCode: 603 }).catch(() => {});
      this.session = null;
      this.setStatus("ready", null);
    }
  }

  async hangup() {
    const session = this.session;
    if (!session) return;
    if (session.state === SessionState.Established) {
      await session.bye().catch(() => {});
    } else if (session instanceof Inviter) {
      await session.cancel().catch(() => {});
    } else if (session instanceof Invitation) {
      await this.decline();
    }
  }

  toggleMute(): boolean {
    const session = this.session;
    if (!session) return false;
    const sdh = session.sessionDescriptionHandler as unknown as {
      peerConnection?: RTCPeerConnection;
    };
    this.muted = !this.muted;
    sdh?.peerConnection?.getSenders().forEach((sender) => {
      if (sender.track) sender.track.enabled = !this.muted;
    });
    return this.muted;
  }

  async destroy() {
    try {
      await this.hangup();
      await this.registerer?.unregister().catch(() => {});
      await this.userAgent?.stop();
    } finally {
      this.userAgent = null;
      this.registerer = null;
      this.session = null;
      this.setStatus("offline", null);
    }
  }
}
